<template>
  <Layout>
      <div class="page-header">
        <div class="page-title">
          <h2>{{$t('homePage')}}</h2>
          <h5>{{getGreetings}}, {{ getUserName }}!</h5>
        </div>

        <div class="actions">
          <div v-if="!editMode" class="edit-widget-btn" @click="toggleEditMode">
            <span class="edit-widget-txt">{{$t('edit_widget')}}</span>
          </div>

          <div v-else class="edit-actions">
            <button
                class="add-new-btn"
                @click="addShortcutModalVisible = true"
            >{{$t('add')}}</button>
            <button class="reset-btn" @click="resetWidgets">{{$t('reset_widget')}}</button>
            <button class="save-btn" @click="saveWidgets">{{$t('save')}}</button>
          </div>
        </div>
      </div>

      <draggable
        v-model="mainCards"
        class="dashboard-grid"
        item-key="id"
        :disabled="!editMode"
        ghost-class="ghost-card"
        drag-class="dragging-card"
      >
        <template #item="{ element }">
          <DashboardCard
            :key="element.id"
            :id="element.id"
            :shortcut="element"
            :edit-mode="editMode"
            :icons="icons"
            @delete="handleDeleteMainCard"
          />
        </template>
      </draggable>

      <div class="shortcuts-container">
        <h2 class="shortcuts-title">{{$t('other_shortcuts')}}</h2>

        <draggable
          v-model="shortcuts"
          class="shortcuts-grid"
          item-key="id"
          :disabled="!editMode"
          ghost-class="ghost-shortcut"
          drag-class="dragging-shortcut"
        >
          <template #item="{ element }">
            <ShortcutCard
              :key="element.id"
              :shortcut="element"
              :is-disabled="element.isDisabled"
              :edit-mode="editMode"
              :icons="icons"
              @delete="handleDeleteShortcutCard"
            />
          </template>
        </draggable>
      </div>

    <EditShortcutsModal
        :visible="addShortcutModalVisible"
        :on-hide="() => { addShortcutModalVisible = false }"
        :icons="icons"
        :shortcuts="shortcutList"
        @update-shortcuts="handleUpdateShortcuts"
    />
  </Layout>
</template>

<script>
import draggable from 'vuedraggable';
import DashboardCard from "@/components/DashboardCard.vue";
import ShortcutCard from "@/components/ShortcutCard.vue";
import Layout from "@/components/layouts/Layout.vue";
import EditShortcutsModal from "@/components/modals/EditShortcutsModal.vue";
import FileUploadFillIcon from "@/assets/images/fileUploadFill.svg";
import AddFillIcon from "@/assets/images/addFill.svg";
import LightningFillIcon from "@/assets/images/lightningFill.svg";
import ChartFillIcon from "@/assets/images/chartFill.svg";
import ReportsFillIcon from "@/assets/images/reportsFill.svg";
import {flattenedMenuRoutes} from "@/router/menuRoutes.js";

export default {
  name: 'Home',
  components: {
    EditShortcutsModal,
    DashboardCard,
    ShortcutCard,
    Layout,
    draggable
  },
  data() {
    return {
      editMode: false,
      addShortcutModalVisible: false,
      defaultMainCards: [
          'create_program',
          'create_nano_course',
          'transform_document',
          'dashboard',
          'reports',
      ],
      defaultShortcutList: [
          'programs',
          'selfAssessments',
          'gameLabs',
          'blogs',
          'notifications',
          'dashboard',
          'ruleSet',
          'companies',
          'users',
          'experts',
          'journeys',
          'files',
          'reviews',
          'pricing',
          'onboardings',
          'pages',
          'tags'
      ],

      mainCards: [],
      shortcuts: [],

      shortcutList: [
        {
          id: 'create_program',
          title: this.$t('create_program'),
          tagColor: 'orange',
          tagText: 'Program',
          iconRef: 'programCreateIcon',
          action: '/program/new'
        },
        {
          id: 'create_nano_course',
          title: this.$t('create_nano_course'),
          tagColor: 'orange',
          tagText: 'Program',
          iconRef: 'lightningFill',
          action: '/programs?createNanoCourseModal=true',
        },
        {
          id: 'transform_document',
          title: this.$t('transform_document'),
          tagColor: 'orange',
          tagText: 'Program',
          iconRef: 'fileUpload',
          action: '/programs?createProgramModal=true',
        },
        {
          id: 'dashboard',
          title: this.$t('dashboard'),
          iconRef: 'chartFill',
          action: '/dashboard'
        },
        {
          id: 'reports',
          title: this.$t('reports.reports_title'),
          iconRef: 'reportsFill',
          action: '/reports'
        }
      ],

      icons: {
        fileUpload: FileUploadFillIcon,
        addFill: AddFillIcon,
        lightningFill: LightningFillIcon,
        chartFill: ChartFillIcon,
        reportsFill: ReportsFillIcon,
      },
      flattenedMenuRoutes
    }
  },
  mounted() {
    this.initMainCards();
    this.initShortcuts();
  },
  methods: {
    toggleEditMode() {
      this.editMode = !this.editMode;
    },
    initMainCards() {
      const localStorageCards = JSON.parse(localStorage.getItem('mainCards'));
      if (localStorageCards) {
        this.mainCards = localStorageCards.map(id => {
          const shortcut = this.shortcutList.find(item => item.id === id);
          return {
            ...shortcut,
            isDisabled: false
          };
        });
      } else {
        this.mainCards = this.defaultMainCards.map(id => {
          const shortcut = this.shortcutList.find(item => item.id === id);
          return {
            ...shortcut,
            isDisabled: false
          };
        });
      }
    },
    initShortcuts() {
      const localStorageShortcuts = JSON.parse(localStorage.getItem('shortcuts'));

      if (localStorageShortcuts) {
        this.shortcuts = localStorageShortcuts.map(id => {
          const shortcut = this.flattenedMenuRoutes.find(item => item.id === id);
          return {
            ...shortcut,
            isDisabled: false
          };
        });
      } else {
        this.shortcuts = this.defaultShortcutList.map(id => {
          const shortcut = this.flattenedMenuRoutes.find(item => item.id === id);
          return {
            ...shortcut,
            isDisabled: false
          };
        });
      }
    },
    saveWidgets() {
      const mainCards = this.mainCards.map(item => item.id);
      localStorage.setItem('mainCards', JSON.stringify(mainCards));

      const shortcuts = this.shortcuts.map(item => item.id);
      localStorage.setItem('shortcuts', JSON.stringify(shortcuts));
      this.editMode = false;
    },
    resetWidgets() {
      localStorage.removeItem('mainCards');
      localStorage.removeItem('shortcuts');
      this.initMainCards();
      this.initShortcuts();
    },
    handleDeleteMainCard(id) {
      this.mainCards = this.mainCards.filter(item => item.id !== id);
    },
    handleDeleteShortcutCard(id) {
      this.shortcuts = this.shortcuts.filter(item => item.id !== id);
    },
    handleUpdateShortcuts(selectedIds) {
      this.mainCards = selectedIds.map(id => {
        const shortcut = this.shortcutList.find(item => item.id === id);
        return {
          ...shortcut,
          isDisabled: false
        };
      });
    }
  },
  computed: {
    getGreetings() {
      const now = new Date();
      const currentHour = now.getHours();

      if (currentHour >= 5 && currentHour < 12) {
        return this.$t('goodMorning');
      } else if (currentHour >= 12 && currentHour < 17) {
        return this.$t('goodAfternoon');
      } else if (currentHour >= 17 && currentHour < 22) {
        return this.$t('goodEvening');
      } else {
        return this.$t('goodNight');
      }
    },
    getUserName() {
      const user = localStorage.getItem('user');
      if (user) {
        const userData = JSON.parse(user);
        return userData.name || '';
      }
    }
  }


}
</script>

<style scoped>
.welcome-container {
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 24px;
}

.welcome-view {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}
.edit-widget-btn {
  border: 1px solid #EAECF0;
  background-color: #fff;
  border-radius: 9px;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 36px;
  padding: 0 12px;
}

.edit-widget-txt {
  font-family: euclid_regular, sans-serif;
  font-size: 14px;
  color: #040C2D;
  letter-spacing: 0;
}

.dashboard-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 16px;
  width: 100%;
}

@media (min-width: 768px) {
  .dashboard-grid {
    grid-template-columns: repeat(5, 1fr);
  }
}

.shortcuts-container {
  margin-top: 40px;
  width: 100%;
}

.shortcuts-title {
  font-family: euclid_medium, sans-serif;
  font-size: 20px;
  line-height: 20px;
  color: #101828;
  margin-bottom: 20px;
}

.shortcuts-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
}

@media (max-width: 768px) {
  .shortcuts-grid {
    grid-template-columns: 1fr;
  }
}

.edit-actions {
  display: flex;
  gap: 10px;
}

.add-new-btn, .reset-btn {
  background-color: #FFFFFF;
  color: #101828;
  border: 1px solid #D0D5DD;
  border-radius: 9px;
  height: 36px;
  padding: 0 12px;
  font-size: 14px;
  cursor: pointer;
}

.reset-btn {
  color: #B42318;
}

.reset-btn:hover {
  background-color: #FEF3F2;
}

.save-btn {
  background-color: #0044FF;
  color: #FFFFFF;
  border: none;
  font-size: 14px;
  cursor: pointer;
  border-radius: 9px;
  height: 36px;
  padding: 0 12px;
}

.ghost-card {
  border: 1px dashed #98A2B3 !important;
  background-color: #F2F4F7 !important;
  box-shadow: none !important;
  border-radius: 12px;
}

.ghost-shortcut {
  border: 2px dashed #0070F0 !important;
  background-color: rgba(0, 112, 240, 0.05) !important;
  box-shadow: none !important;
  border-radius: 12px;
  opacity: 0.7;
}

.dragging-card {
  transform: rotate(3deg);
  cursor: grabbing;
}

.dragging-shortcut {
  cursor: grabbing;
}

.dashboard-grid > div,
.shortcuts-grid > div {
  min-height: 60px;
}
</style>

